import { call, put, takeLatest } from "redux-saga/effects";
import request, { hashfameRequest } from "utils/request";
import { usersActions } from "./index";
import { showToast } from "components/ToastContainer/index";

function* fetchUsersList(action) {
  try {
    const response = yield call(request, `/api/users`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(action.payload),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });
    yield put(usersActions.fetchAllUsersSuccess(response));
  } catch (error) {
    yield put(usersActions.fetchAllUsersFailure(error.message));
  }
}

function* fetchUserSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(request, `/api/users/${userId}`, {
      method: "GET",
      credentials: "include"
    });
    yield put(usersActions.fetchUserByIdSuccess(response.user));
  } catch (error) {
    yield put(usersActions.fetchUserByIdFailure(error.message));
    yield call(showToast, {
      type: "error",
      message: "Failed to fetch user details."
    });
  }
}

function* activateUserSaga(action) {
  try {
    const queryString = Object.keys(action.payload)
      .map((key) => `${key}=${action.payload[key]}`)
      .join("&");

    const response = yield call(
      hashfameRequest,
      `/api/v1/admin.activate.user?${queryString}`,
      {
        method: "GET"
      }
    );
    yield put(usersActions.userActivateSuccess(response));
    showToast("User activated successfully.", "success");
  } catch (error) {
    yield put(usersActions.userActivateFailure(error.message));
    showToast("Failed to activate user.", "danger");
  }
}

function* deactivateUserSaga(action) {
  try {
    const queryString = Object.keys(action.payload)
      .map((key) => `${key}=${action.payload[key]}`)
      .join("&");

    const response = yield call(
      hashfameRequest,
      `/api/v1/admin.user.disable?${queryString}`,
      {
        method: "GET"
      }
    );
    yield put(usersActions.userDeactivateSuccess(response));
    showToast("User deactivated successfully.", "success");
  } catch (error) {
    yield put(usersActions.userDeactivateFailure(error.message));
    showToast("Failed to deactivate user.", "danger");
  }
}

function* verifyEmailAndActivateSaga(action) {
  try {
    const queryString = Object.keys(action.payload)
      .map((key) => `${key}=${action.payload[key]}`)
      .join("&");

    const response = yield call(
      hashfameRequest,
      `/api/v1/admin.approve.public.user?${queryString}`,
      {
        method: "POST"
      }
    );
    yield put(usersActions.verifyEmailAndActivateSuccess(response));
    showToast("Email verified and user activated successfully.", "success");
  } catch (error) {
    yield put(usersActions.verifyEmailAndActivateFailure(error.message));
    showToast(
      error.message || "Failed to verify email and activate user.",
      "danger"
    );
  }
}

function* updateUserInfoSaga(action) {
  try {
    const response = yield call(
      request,
      `/api/users/${action.payload.user_id}`,
      {
        method: "PATCH",
        body: JSON.stringify(action.payload.data),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        }
      }
    );
    yield put(usersActions.userUpdateSuccess(response));
    showToast("User information updated successfully.", "success");
  } catch (error) {
    yield put(usersActions.userUpdateFailure(error.message));
    showToast(error.message || "Failed to update user information.", "danger");
  }
}

function* getSearchHistorySaga(action) {
  try {
    const response = yield call(request, `/api/users/search.history`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(action.payload),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });
    yield put(usersActions.getSearchHistorySuccess(response));
  } catch (error) {
    yield put(usersActions.getSearchHistoryFailure(error.message));
    showToast("Failed to fetch user search history.", "danger");
  }
}

function* uploadUserProfileImageSaga(action) {
  try {
    const { file, user_id } = action.payload;

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("file", file);

    // Upload the profile image
    const response = yield call(
      request,
      `/api/users/${user_id}/profile.image`,
      {
        method: "POST",
        body: formData
      }
    );

    yield put(usersActions.uploadProfileImageSuccess(response));
    showToast("Profile image updated successfully.", "success");
  } catch (error) {
    yield put(usersActions.uploadProfileImageFailure(error.message));
    showToast(error.message || "Failed to upload profile image.", "danger");
  }
}

function* fetchCreatorAnalyticsSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(request, `/api/user/${userId}/analytics`, {
      method: "GET",
      credentials: "include"
    });
    yield put(usersActions.fetchCreatorAnalyticsSuccess(response));
  } catch (error) {
    yield put(usersActions.fetchCreatorAnalyticsFailure(error.message));
    showToast("Failed to fetch creator analytics.", "danger");
  }
}

function* fetchCreatorChartSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(request, `/api/user/${userId}/chart`, {
      method: "GET",
      credentials: "include"
    });
    yield put(usersActions.fetchCreatorChartSuccess(response));
  } catch (error) {
    yield put(usersActions.fetchCreatorChartFailure(error.message));
    showToast("Failed to fetch creator chart.", "danger");
  }
}

function* fetchMarketplaceAnalyticsSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(
      request,
      `/api/user/${userId}/marketplace/analytics`,
      {
        method: "GET",
        credentials: "include"
      }
    );
    yield put(usersActions.fetchMarketplaceAnalyticsSuccess(response));
  } catch (error) {
    yield put(usersActions.fetchMarketplaceAnalyticsFailure(error.message));
    showToast("Failed to fetch marketplace analytics.", "danger");
  }
}

function* fetchMarketplaceChartSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(
      request,
      `/api/user/${userId}/marketplace/chart`,
      {
        method: "GET",
        credentials: "include"
      }
    );
    yield put(usersActions.fetchMarketplaceChartSuccess(response));
  } catch (error) {
    yield put(usersActions.fetchMarketplaceChartFailure(error.message));
    showToast("Failed to fetch marketplace chart.", "danger");
  }
}

function* fetchCompanyUsersByStatusSaga(action) {
  try {
    const response = yield call(request, `/api/users/company-status`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(action.payload),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });

    yield put(usersActions.fetchCompanyUsersByStatusSuccess(response));
  } catch (error) {
    yield put(usersActions.fetchCompanyUsersByStatusFailure(error.message));

    showToast(error.message || "Failed to fetch company users.", "danger");
  }
}

export function* usersSaga() {
  yield takeLatest(usersActions.fetchAllUsers.type, fetchUsersList);
  yield takeLatest(usersActions.fetchUserById.type, fetchUserSaga);
  yield takeLatest(usersActions.userActivate.type, activateUserSaga);
  yield takeLatest(usersActions.userDeactivate.type, deactivateUserSaga);
  yield takeLatest(
    usersActions.verifyEmailAndActivate.type,
    verifyEmailAndActivateSaga
  );
  yield takeLatest(usersActions.userUpdate.type, updateUserInfoSaga);
  yield takeLatest(usersActions.getSearchHistory.type, getSearchHistorySaga);
  yield takeLatest(
    usersActions.uploadProfileImage.type,
    uploadUserProfileImageSaga
  );
  yield takeLatest(
    usersActions.fetchCreatorAnalytics.type,
    fetchCreatorAnalyticsSaga
  );
  yield takeLatest(usersActions.fetchCreatorChart.type, fetchCreatorChartSaga);
  yield takeLatest(
    usersActions.fetchMarketplaceAnalytics.type,
    fetchMarketplaceAnalyticsSaga
  );
  yield takeLatest(
    usersActions.fetchMarketplaceChart.type,
    fetchMarketplaceChartSaga
  );

  yield takeLatest(
    usersActions.fetchCompanyUsersByStatus.type,
    fetchCompanyUsersByStatusSaga
  );
}
